import { Users, Ship, IdCard, MoonStar, Palmtree, UtensilsCrossed } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { CtaBanner } from "@/components/CtaBanner";
import { Reveal, Overline } from "@/components/motion";

const DAYS = [
  {
    day: "Vineri",
    title: "Plecare spre capitala mării",
    desc: "Îmbarcare dimineața în Marina Life Harbour, Limanu. Aproximativ 40 de mile marine cu pânzele, prânz la bord și baie la ancoră pe drum. Seara: acostare în marina Varna și cină pe faleza portului.",
  },
  {
    day: "Sâmbătă",
    title: "Grădina Mării & orașul",
    desc: "Micul dejun la bord, apoi Grădina Mării — cel mai mare parc de la Marea Neagră — plaja din Varna și, la alegere, Delfinariumul sau Muzeul de Arheologie cu faimoasa comoară de aur. Seara liberă în oraș, noapte la bord.",
  },
  {
    day: "Duminică",
    title: "Baia de rămas-bun",
    desc: "Dimineața pe plajă sau la ultima cafea pe faleză, apoi ridicăm pânzele spre nord. Navigăm cu soarele în pupa și sosim în Limanu la apus.",
  },
];

const INFO = [
  { icon: Users, title: "Grupuri de 8", text: "Excursiile se fac cu 8 persoane la bord — grupul perfect pentru un weekend între prieteni." },
  { icon: Ship, title: "~40 de mile marine", text: "Navigație reală cu pânzele, dus-întors. Nu e un transfer, e jumătate din distracție." },
  { icon: IdCard, title: "Documente", text: "Carte de identitate sau pașaport valabil. Formalitățile de graniță pe apă le rezolvăm noi." },
  { icon: MoonStar, title: "Cazare la bord", text: "Două nopți în cabinele lui Hope, în marina Varna — adormi și te trezești pe apă." },
  { icon: Palmtree, title: "Grădina Mării", text: "Cel mai mare parc de la Marea Neagră, întins kilometri de-a lungul falezei." },
  { icon: UtensilsCrossed, title: "Bucătăria locală", text: "Taverne pe faleză, pește proaspăt și vinuri bulgărești — prețuri prietenoase." },
];

const VARNA_PHOTOS = [
  {
    src: "/hope/varna-2.jpg",
    alt: "Portul Varna cu veliere clasice acostate la chei",
    caption: "Portul Varna — velierele te întâmpină",
    span: "md:col-span-7 h-[340px] md:h-[460px]",
  },
  {
    src: "/hope/varna-3.jpg",
    alt: "Veliere în marina Varna în lumina apusului",
    caption: "Marina în ora de aur",
    span: "md:col-span-5 h-[340px] md:h-[460px]",
  },
  {
    src: "/hope/varna-4.jpg",
    alt: "Farul de la capătul digului din Varna, cu marina în fundal",
    caption: "Farul de la capătul digului",
    span: "md:col-span-5 h-[320px] md:h-[420px]",
  },
  {
    src: "/hope/varna-5.png",
    alt: "Plajele cu nisip auriu și stațiunile de lângă Varna, văzute de sus",
    caption: "Plajele de aur ale Varnei",
    span: "md:col-span-7 h-[320px] md:h-[420px]",
  },
];

const Varna = () => (
  <div data-testid="varna-page">
    <PageHero
      testid="varna-hero"
      image="/hope/varna-1.jpg"
      alt="Plaja și stațiunile de lângă Varna, văzute de sus, cu mare turcoaz"
      overline="Excursie de Weekend · Bulgaria"
      titleLines={["Varna,", "capitala mării."]}
      subtitle="Plecare vineri dimineața din Limanu, retur duminică la apus. 8 locuri la bord, două nopți în marina Varna și cel mai frumos parc de la Marea Neagră."
    />

    <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
      <Reveal className="mb-16">
        <Overline className="mb-6">Programul Weekendului</Overline>
        <h2 className="font-serif text-3xl font-light text-ivory sm:text-4xl lg:text-5xl">
          Vineri — Duminică, <span className="italic text-gold">Limanu → Varna → Limanu.</span>
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {DAYS.map((d, i) => (
          <Reveal key={d.day} delay={i * 0.12}>
            <div className="flex h-full flex-col border border-white/10 p-8 transition-colors duration-500 hover:border-gold/40 lg:p-10" data-testid={`varna-day-${d.day.toLowerCase()}`}>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{d.day}</p>
              <h3 className="mt-5 font-serif text-2xl text-ivory">{d.title}</h3>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-mist">{d.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-16 flex flex-col items-start justify-between gap-6 border border-gold/30 p-8 sm:flex-row sm:items-center lg:p-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Tarif orientativ</p>
            <p className="mt-3 font-serif text-3xl font-light text-ivory">
              De la 100 € <span className="text-lg text-mist">/ persoană / weekend</span>
            </p>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-mist">
            Include skipper, combustibil, taxe portuare și cazare la bord.
            Weekenduri din mai până în octombrie 2027, la cerere.
          </p>
        </div>
      </Reveal>
    </section>

    <section data-testid="varna-gallery" className="border-t border-white/10 bg-deep">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <Reveal className="mb-16 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <Overline className="mb-6">Varna în Imagini</Overline>
            <h2 className="font-serif text-3xl font-light text-ivory sm:text-4xl lg:text-5xl">
              Orașul care te <span className="italic text-gold">primește cu pânze.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-mist">
            Portul cu veliere, farul de pe dig, marina la apus și golful văzut
            de sus — destinația weekendului vostru.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
          {VARNA_PHOTOS.map((photo, i) => (
            <Reveal key={photo.src} delay={i * 0.08} className={photo.span}>
              <figure
                data-testid={`varna-photo-${i + 1}`}
                className="group relative h-full w-full overflow-hidden border border-white/10"
              >
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-abyss/80 via-transparent to-transparent" />
                <figcaption className="absolute bottom-0 left-0 p-5 text-[10px] uppercase tracking-[0.25em] text-ivory/80 lg:p-6 lg:text-xs">
                  {photo.caption}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="border-y border-white/10 bg-deep">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <Reveal className="mb-16">
          <Overline className="mb-6">Bun de Știut</Overline>
          <h2 className="font-serif text-3xl font-light text-ivory sm:text-4xl">
            Totul pregătit, <span className="italic text-gold">voi doar urcați.</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {INFO.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.06} className="bg-deep">
              <div className="h-full p-8" data-testid={`varna-info-${i + 1}`}>
                <item.icon className="h-5 w-5 text-gold" />
                <h3 className="mt-5 font-serif text-lg text-ivory">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mist">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CtaBanner
      title="8 locuri. Un weekend. Varna."
      subtitle="Weekendurile de vară se ocupă repede — spune-ne data și ținem barca pregătită spre sud."
    />
  </div>
);

export default Varna;
